import dayjs, { Dayjs } from 'dayjs';
import {
  dateFormatRange,
  fromHoursStringToDayjs,
  isToday,
} from '../../../helpers/dateTimes';
import {
  CmsConfigs,
  DateTimePickerModel,
  TimePickerPreset,
} from '../../../models';
import {
  calculateTimeSpansByRanges,
  getValidPresets,
  getValidPresetSlot,
  isTimeInRange,
} from '../chooseDeliveryTimeHelpers';
import { TimeSpan } from './HourSlider/HourSlider';

export enum TimePickerAction {
  EditPreset,
  EditDate,
  EditStartTime,
  EditEndTime,
}

export type TimePickerSavedData = {
  date?: string;
  startTime?: TimeSpan;
  endTime?: TimeSpan;
};

export type TimePickerActionValue = {
  presetIndex?: number;
  date?: Dayjs;
  startTimeIndex?: number;
  endTimeIndex?: number;
};

export type TimePickerActionResult = {
  date: Dayjs;
  startTimes: TimeSpan[];
  startTimeIndex: number;
  endTimes: TimeSpan[];
  endTimeIndex: number;
  presets: TimePickerPreset[];
  presetIndex: number;
  price: number;
  originalPrice: number;
};

export class TimePickerController {
  private date!: Dayjs;

  private startTimes!: TimeSpan[];
  private startTimeIndex!: number;

  private endTimes!: TimeSpan[];
  private endTimeIndex!: number;

  private presets: TimePickerPreset[];
  private presetIndex: number;

  private price!: number;
  private originalPrice!: number;

  constructor(
    private readonly dateTimePickerModel: DateTimePickerModel,
    private readonly cmsConfigs: CmsConfigs,
    operationSavedData?: {
      date: string;
      startTime: TimeSpan;
      endTime: TimeSpan;
    }
  ) {
    this.presets = cmsConfigs.timePickerPredefinedSelections!;
    this.presetIndex = this.presets.findIndex((p) => !p.timeRangeEnd);
    this.initialize(operationSavedData);
    this.setPriceAdicentTime();
    this.setPrice();
  }

  public readonly exec = (
    action: TimePickerAction,
    newVal: TimePickerActionValue
  ): TimePickerActionResult => {
    switch (action) {
      case TimePickerAction.EditStartTime:
        this.startTimeAction(newVal.startTimeIndex!);
        break;
      case TimePickerAction.EditEndTime:
        this.endTimeAction(newVal.endTimeIndex!);
        break;
      case TimePickerAction.EditDate:
        this.dateAction(newVal.date!);
        break;
      case TimePickerAction.EditPreset:
        this.presetAction(newVal.presetIndex!);
        break;
    }
    this.setPriceAdicentTime();
    this.setPrice();
    return this.getActionReturnValue();
  };

  private readonly startTimeAction = (newStartTimeIndex: number) => {
    this.startTimeIndex = newStartTimeIndex;

    const stringDate = this.date.format(dateFormatRange);
    const linkedEndTimes =
      this.dateTimePickerModel!.startTimeRanges[stringDate][
        this.startTimes[newStartTimeIndex].time
      ];
    const newEndTimes = Object.keys(linkedEndTimes);
    newEndTimes.sort();
    const calculatedEndSpans = calculateTimeSpansByRanges(newEndTimes);
    this.endTimes = calculatedEndSpans;

    this.updatePreset(this.startTimes[this.startTimeIndex].time);

    return this.getActionReturnValue();
  };

  private readonly endTimeAction = (newEndTimeIndex: number) => {
    this.endTimeIndex = newEndTimeIndex;

    const stringDate = this.date!.format(dateFormatRange);
    const linkedStartTimes =
      this.dateTimePickerModel!.endTimeRanges[stringDate][
        this.endTimes[newEndTimeIndex].time
      ];
    const newStartTimes = Object.keys(linkedStartTimes);
    newStartTimes.sort();
    const calculatedStartSpans = calculateTimeSpansByRanges(newStartTimes);
    this.startTimes = calculatedStartSpans;

    this.updatePreset(this.endTimes[this.endTimeIndex].time);

    return this.getActionReturnValue();
  };

  private readonly dateAction = (newDate: Dayjs) => {
    this.date = newDate;
    const stringDate = newDate.format(dateFormatRange);
    const slot = this.dateActionSetupPresets(newDate);
    this.dateActionSetupSpans(slot, stringDate);
    let from: string;
    let to: string;
    if (slot) {
      from = slot.from.format('HH:mm');
      to = slot.to.format('HH:mm');
    } else {
      ({ from, to } = this.getDefaultSlot(stringDate));
    }
    this.startTimeIndex = this.startTimes.findIndex((st) => st.time === from);
    this.endTimeIndex = this.endTimes.findIndex((et) => et.time === to);
  };

  private readonly presetAction = (newPresetIndex: number) => {
    this.presetIndex = newPresetIndex;
    const selectedPreset = this.getPreset();
    if (selectedPreset.isCurrentDay) this.date = dayjs();
    const stringDate = this.date.format(dateFormatRange);
    const slot = getValidPresetSlot(
      this.dateTimePickerModel,
      this.date,
      selectedPreset
    );
    let from: string;
    let to: string;
    if (slot) {
      from = slot.from.format('HH:mm');
      to = slot.to.format('HH:mm');
    } else {
      from = Object.keys(
        this.dateTimePickerModel.startTimeRanges[stringDate]
      ).sort()[0];
      const tempEndKeys = Object.keys(
        this.dateTimePickerModel.startTimeRanges[stringDate][from]
      ).sort();
      to = tempEndKeys[tempEndKeys.length - 1];
    }
    const { startTimesKeys, endTimesKeys } = this.getTimeKeysBySlot(
      { from, to },
      stringDate
    );
    this.setTimesByKeys(startTimesKeys, endTimesKeys);

    this.startTimeIndex = this.startTimes.findIndex((st) => st.time === from);
    this.endTimeIndex = this.endTimes.findIndex((et) => et.time === to);
  };

  private readonly updatePreset = (timeToCheck: string) => {
    const selectedPreset = this.getPreset();
    if (selectedPreset.timeRangeEnd && selectedPreset.timeRangeStart) {
      const from = fromHoursStringToDayjs(selectedPreset.timeRangeStart);
      const to = fromHoursStringToDayjs(selectedPreset.timeRangeEnd);
      const isPresetValid = isTimeInRange(timeToCheck, from, to);
      if (!isPresetValid) {
        const personalizedIndex = this.presets.findIndex(
          (p) => !p.timeRangeEnd
        );
        this.presetIndex = personalizedIndex;
      }
    }
  };

  public readonly getActionReturnValue = (): TimePickerActionResult => ({
    date: this.date,
    endTimeIndex: this.endTimeIndex,
    endTimes: this.endTimes,
    presetIndex: this.presetIndex,
    presets: this.presets,
    price: this.price,
    originalPrice: this.originalPrice,
    startTimeIndex: this.startTimeIndex,
    startTimes: this.startTimes,
  });

  public readonly getState = () => ({
    date: this.date,
    endTime: this.endTimes[this.endTimeIndex],
    startTime: this.startTimes[this.startTimeIndex],
    price: this.price,
  });

  private readonly setPrice = () => {
    const stringDate = this.date!.format(dateFormatRange);
    const startTime = this.startTimes[this.startTimeIndex];
    const endTime = this.endTimes[this.endTimeIndex];
    this.price =
      this.dateTimePickerModel!.startTimeRanges[stringDate][startTime.time][
        endTime.time
      ];
    this.originalPrice =
      this.dateTimePickerModel!.startOriginalTimeRanges[stringDate][
        startTime.time
      ][endTime.time];
  };

  private readonly priceCalculator = ({
    // compareIndex,
    array,
    isStart = true,
  }: {
    compareIndex: number;
    array: TimeSpan[];
    isStart?: boolean;
  }) => {
    const tempStart = array.map((a) => {
      // const diff = Math.abs(compareIndex - i);
      if (
        a.isEnable
        //  && (diff === 1 || diff === 2)
      ) {
        const startTime = isStart ? a : this.startTimes[this.startTimeIndex];
        const endTime = !isStart ? a : this.endTimes[this.endTimeIndex];
        const stringDate = this.date!.format(dateFormatRange);
        const temPrice =
          this.dateTimePickerModel!.startTimeRanges[stringDate][startTime.time][
            endTime.time
          ];
        return {
          ...a,
          price: temPrice ? temPrice.toFixed(2).replace('.', ',') : '0,00',
        };
      } else {
        return a;
      }
    });

    return tempStart;
  };

  private readonly setPriceAdicentTime = () => {
    const tempStart = this.priceCalculator({
      array: this.startTimes,
      compareIndex: this.startTimeIndex,
    });
    this.startTimes = tempStart;

    const tempEnd = this.priceCalculator({
      array: this.endTimes,
      compareIndex: this.endTimeIndex,
      isStart: false,
    });
    this.endTimes = tempEnd;
  };

  private readonly getPreset = () => {
    if (this.presetIndex >= this.presets.length) {
      return this.presets[this.presetIndex % this.presets.length];
    } else {
      return this.presets[this.presetIndex];
    }
  };

  private readonly dateActionSetupPresets = (newDate: Dayjs) => {
    const selectedPreset = this.getPreset();
    const validPresets = getValidPresets(
      this.dateTimePickerModel,
      newDate,
      this.cmsConfigs.timePickerPredefinedSelections!
    );
    this.presets = validPresets;
    let isSelectedValid: boolean;
    if (selectedPreset.isCurrentDay && !isToday(newDate)) {
      isSelectedValid = false;
    } else {
      const currentValidPreset = validPresets.find(
        (p) => p.id === selectedPreset.id
      );
      isSelectedValid = currentValidPreset !== undefined;
    }

    if (isSelectedValid) {
      const slot = getValidPresetSlot(
        this.dateTimePickerModel,
        newDate,
        selectedPreset
      );
      if (slot) {
        this.presetIndex = this.presets.findIndex(
          (p) => p.id === selectedPreset.id
        );
        return slot;
      } else {
        this.presetIndex = 0;
        return null;
      }
    } else {
      this.presetIndex = 0;
      return null;
    }
  };

  private readonly dateActionSetupSpans = (
    slot: { from: Dayjs; to: Dayjs } | null,
    stringDate: string
  ) => {
    let startTimesKeys;
    let endTimesKeys;

    if (slot) {
      ({ startTimesKeys, endTimesKeys } = this.getTimeKeysBySlot(
        { from: slot.from.format('HH:mm'), to: slot.to.format('HH:mm') },
        stringDate
      ));
    } else {
      const { from, to } = this.getDefaultSlot(stringDate);
      startTimesKeys = Object.keys(
        this.dateTimePickerModel.endTimeRanges[stringDate][to]
      );
      endTimesKeys = Object.keys(
        this.dateTimePickerModel.startTimeRanges[stringDate][from]
      );
    }
    this.setTimesByKeys(startTimesKeys, endTimesKeys);
  };

  private readonly getDefaultSlot = (stringDate: string) => {
    return this.dateTimePickerModel.defaultRanges.find(
      (dr) => dr.date === stringDate
    )!;
  };

  private readonly getTimeKeysBySlot = (
    slot: {
      from: string;
      to: string;
    },
    stringDate: string
  ) => {
    let startTimesKeys: string[];
    let endTimesKeys: string[];
    startTimesKeys = Object.keys(
      this.dateTimePickerModel.endTimeRanges[stringDate][slot.to]
    );
    endTimesKeys = Object.keys(
      this.dateTimePickerModel.startTimeRanges[stringDate][slot.from]
    );

    return { startTimesKeys, endTimesKeys };
  };

  private readonly setTimesByKeys = (
    startTimesKeys: string[],
    endTimesKeys: string[]
  ) => {
    startTimesKeys.sort();
    endTimesKeys.sort();
    this.startTimes = calculateTimeSpansByRanges(startTimesKeys);
    this.endTimes = calculateTimeSpansByRanges(endTimesKeys);
  };

  private readonly initialize = (
    operationSavedData: TimePickerSavedData | undefined
  ) => {
    const isInOperation =
      operationSavedData &&
      operationSavedData.date &&
      operationSavedData.endTime &&
      operationSavedData.startTime;

    if (isInOperation) {
      const dateToSetup = dayjs(operationSavedData.date);
      this.dateAction(dateToSetup);
      const startTimeSpanIndex = this.startTimes.findIndex(
        (st) => st.time === operationSavedData.startTime!.time
      );
      const endTimeSpanIndex = this.endTimes.findIndex(
        (et) => et.time === operationSavedData.endTime!.time
      );
      if (startTimeSpanIndex >= 0 && endTimeSpanIndex >= 0) {
        this.startTimeAction(startTimeSpanIndex);
        this.endTimeAction(endTimeSpanIndex);
      }
    } else {
      this.dateAction(this.dateTimePickerModel.availableDates![0]);
    }
  };
}
